import { useQuery } from "@tanstack/react-query";
import { getBrowserSupabaseClient } from "@wtc/supabase";

export interface TicketUpdateAuthor {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

export interface TicketUpdateRow {
  id: string;
  ticket_id: string;
  user_id: string;
  update_type: string;
  content: string;
  is_internal: boolean | null;
  created_at: string | null;
  author: TicketUpdateAuthor | null;
}

function authorDisplayName(author: TicketUpdateAuthor | null): string {
  if (!author) return "Unknown user";
  const name = `${author.first_name ?? ""} ${author.last_name ?? ""}`.trim();
  return name || author.email || "Unknown user";
}

export { authorDisplayName };

/**
 * All updates for a ticket, newest first, with author attribution.
 * Query key matches the invalidation in `useCreateTicketUpdate`
 * (["ticket-updates", ticketId]) so new updates appear immediately.
 */
export function useTicketUpdates(ticketId: string | undefined) {
  return useQuery({
    queryKey: ["ticket-updates", ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<TicketUpdateRow[]> => {
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase
        .from("ticket_updates")
        .select(
          `
          id,
          ticket_id,
          user_id,
          update_type,
          content,
          is_internal,
          created_at,
          app_users!ticket_updates_user_id_fkey(
            id,
            email,
            first_name,
            last_name
          )
        `
        )
        .eq("ticket_id", ticketId as string)
        .order("created_at", { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return (data ?? []).map((row) => {
        const rawAuthor = row.app_users as unknown;
        const author = Array.isArray(rawAuthor)
          ? ((rawAuthor[0] ?? null) as TicketUpdateAuthor | null)
          : ((rawAuthor ?? null) as TicketUpdateAuthor | null);
        return {
          id: row.id,
          ticket_id: row.ticket_id,
          user_id: row.user_id,
          update_type: row.update_type,
          content: row.content,
          is_internal: row.is_internal,
          created_at: row.created_at,
          author,
        };
      });
    },
  });
}
